import React, { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { QuestionsAndAnswers } from '../../../../components/society/QuestionsAndAnswers'
import { ViewBase } from '../../../../components/common/ViewBase'
// eslint-disable-next-line max-len
import { CreateUpdateQuestionsAndAnswersSectionModal } from '../../../../components/society/QuestionsAndAnswers/QuestionsAndAnswersSection/CreateUpdateQuestionsAndAnswersSectionModal'
import { useModal } from '../../../../hooks/useModal'
import { ButtonVariant } from '../../../../components/common/Button'
import { IconChoices } from '../../../../components/common/Icon'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { IllustrationChoices } from '../../../../components/common/Illustration'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { faqQueries } from '../../../../api/faqs'
import { i18nSocietyType } from '../../../../i18n'

export const QuestionsAndAnswersView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const userId = useAuthenticatedUserId()
  const { society } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('society:faq', {
      '{{ societyName }}': society?.name,
    })
  )
  const {
    show: showCreateSectionModal,
    open: openCreateSectionModal,
    close: closeCreateSectionModal,
  } = useModal()

  const { data: sections, isLoading: isSectionsLoading } = useQuery(
    faqQueries.getFaqsPerSociety(society?._id)
  )
  const isAdmin = isUserAdminInSociety(society, userId as string)

  const loading = isSectionsLoading

  const errorView =
    !loading && sections && sections.length === 0
      ? {
          title: translate('questionsAndAnswersView.emptyState.title'),
          subtitle: translate('questionsAndAnswersView.emptyState.subtitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          illustration: IllustrationChoices.CHECKLIST,
        }
      : undefined

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('questionsAndAnswersView.title')}
        buttons={[
          ...(isAdmin
            ? [
                {
                  label: translate(
                    'createUpdateQuestionsAndAnswers.addNewSectionTitle'
                  ),
                  variant: ButtonVariant.PRIMARY,
                  icon: IconChoices.PLUS_SIGN,
                  onClick: openCreateSectionModal,
                },
              ]
            : []),
        ]}
        loading={loading}
        errorView={errorView}
      >
        <QuestionsAndAnswers />
      </ViewBase>
      <CreateUpdateQuestionsAndAnswersSectionModal
        show={showCreateSectionModal}
        close={closeCreateSectionModal}
      />
    </ErrorBoundary>
  )
}
